import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Plus, Filter, RefreshCw, Trash2, CheckSquare, ChevronDown, ChevronRight, CheckCheck, X, Tag, Download, ImagePlus, Loader2, Clock } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import TaskModal from '../components/TaskModal';
import FilterBar from '../components/FilterBar';
import TaskTableRow from '../components/TaskTableRow';
import PersonalReminders from '../components/PersonalReminders';
import { ExportButton } from '../utils/exportUtils';
import { offlineDB } from '../utils/offlineDB';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TasksPage = ({ user }) => {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedTasks, setSelectedTasks] = useState([]);

  // Delete approval workflow
  const canApproveTaskDel = user?.is_admin || user?.is_super_admin || user?.permissions?.can_approve_deletions === true;
  const [taskDeleteRequests, setTaskDeleteRequests] = useState([]);
  const [isTaskDeleteDialogOpen, setIsTaskDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [taskDeleteReason, setTaskDeleteReason] = useState('');
  const [taskDeleteImage, setTaskDeleteImage] = useState(null);
  const [taskDeleteImagePreview, setTaskDeleteImagePreview] = useState(null);
  const [submittingTaskDelete, setSubmittingTaskDelete] = useState(false);
  const taskDeleteImageRef = useRef(null);

  const fetchTaskDeleteRequests = async () => {
    if (!canApproveTaskDel) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/delete-requests?status=pending&module=tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaskDeleteRequests(res.data);
    } catch (_e) { /* silent */ }
  };
  const [expandedSections, setExpandedSections] = useState({
    Critical: true,
    High: true,
    Medium: false,
    Low: false,
    Completed: false,
  });
  const [filters, setFilters] = useState({
    category: 'All',
    priority: 'All',
    status: 'All',
    workAssignment: 'All',
  });
  const [taskSearch, setTaskSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  useEffect(() => {
    fetchTasks();
    fetchTaskDeleteRequests();
    window.addEventListener('offlineSyncComplete', fetchTasks);
    return () => window.removeEventListener('offlineSyncComplete', fetchTasks);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [tasks, filters, taskSearch, dateRange]);

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const applyFilters = () => {
    let result = [...tasks];
    if (filters.category !== 'All') result = result.filter((t) => t.category === filters.category);
    if (filters.priority !== 'All') result = result.filter((t) => t.priority === filters.priority);
    if (filters.status !== 'All') result = result.filter((t) => t.status === filters.status);
    if (filters.workAssignment !== 'All') result = result.filter((t) => t.work_assignment === filters.workAssignment);
    if (taskSearch.trim()) {
      const q = taskSearch.toLowerCase();
      result = result.filter((t) =>
        t.title?.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q) ||
        t.assigned_to?.toLowerCase().includes(q)
      );
    }
    // Date range filter
    if (dateRange.from) {
      const from = new Date(dateRange.from);
      from.setHours(0, 0, 0, 0);
      result = result.filter((t) => t.created_at && new Date(t.created_at) >= from);
    }
    if (dateRange.to) {
      const to = new Date(dateRange.to);
      to.setHours(23, 59, 59, 999);
      result = result.filter((t) => t.created_at && new Date(t.created_at) <= to);
    }
    setFilteredTasks(result);
  };

  const handleCreateTask = async (taskData) => {
    if (!navigator.onLine) {
      try {
        await offlineDB.add({
          module: 'task',
          displayName: `Task – ${taskData.title || 'New Task'}`,
          endpoint: '/tasks',
          method: 'POST',
          data: { ...taskData, attachments: [] }, // attachments need upload, skip when offline
        });
        const count = await offlineDB.pendingCount();
        if (window.__setOfflinePendingCount) window.__setOfflinePendingCount(count);
        toast.success('Saved locally — will sync when back online', {
          description: 'Task stored on this device',
          duration: 4000,
        });
        setIsModalOpen(false);
      } catch (_e) {
        toast.error('Failed to save offline. Storage may be full.');
      }
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/tasks`, taskData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Task created successfully!');
      setIsModalOpen(false);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleUpdateTask = async (taskId, taskData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/tasks/${taskId}`, taskData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Task updated successfully!');
      setIsModalOpen(false);
      setEditingTask(null);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (canApproveTaskDel) {
      // Admins/approvers: direct delete
      if (!window.confirm('Are you sure you want to delete this task?')) return;
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/tasks/${taskId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Task deleted successfully!');
        fetchTasks();
        fetchTaskDeleteRequests();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete task');
      }
    } else {
      // Non-admin: open the delete request dialog
      setTaskToDelete(task || { id: taskId });
      setTaskDeleteReason('');
      setTaskDeleteImage(null);
      setTaskDeleteImagePreview(null);
      setIsTaskDeleteDialogOpen(true);
    }
  };

  const handleTaskDeleteImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5 MB)'); return; }
    setTaskDeleteImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setTaskDeleteImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submitTaskDeleteRequest = async () => {
    if (!taskToDelete || !taskDeleteReason.trim()) return;
    setSubmittingTaskDelete(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('module', 'tasks');
      formData.append('record_id', taskToDelete.id);
      formData.append('record_display', `Task: ${taskToDelete.title || taskToDelete.id}`);
      formData.append('reason', taskDeleteReason);
      if (taskDeleteImage) formData.append('proof_image', taskDeleteImage);
      await axios.post(`${API}/delete-requests`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Delete request submitted for approval', {
        description: 'An admin will review your request',
      });
      setIsTaskDeleteDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingTaskDelete(false);
    }
  };

  const handleApproveTaskDelete = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deletion approved');
      fetchTasks();
      fetchTaskDeleteRequests();
    } catch (e) {
      toast.error('Failed to approve');
    }
  };

  const handleRejectTaskDelete = async (reqId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/reject`, { note: 'Rejected' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task delete request rejected');
      fetchTaskDeleteRequests();
    } catch (e) {
      toast.error('Failed to reject');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/tasks/${taskId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Status updated!');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedTasks.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      await Promise.all(
        selectedTasks.map((taskId) =>
          axios.patch(`${API}/tasks/${taskId}/status`, { status: newStatus }, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      toast.success(`${selectedTasks.length} tasks updated!`);
      setSelectedTasks([]);
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update tasks');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTasks.length === 0) return;
    if (!window.confirm(`Delete ${selectedTasks.length} selected tasks?`)) return;
    try {
      const token = localStorage.getItem('token');
      await Promise.all(
        selectedTasks.map((taskId) =>
          axios.delete(`${API}/tasks/${taskId}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      toast.success(`${selectedTasks.length} tasks deleted!`);
      setSelectedTasks([]);
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete tasks');
    }
  };

  const toggleTaskSelection = (taskId) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const groupTasksByPriority = () => {
    const activeTasks = filteredTasks.filter((t) => t.status !== 'completed');
    const completedTasks = filteredTasks.filter((t) => t.status === 'completed');
    const priorities = ['Critical', 'High', 'Medium', 'Low'];
    const grouped = {};
    priorities.forEach((priority) => {
      grouped[priority] = activeTasks.filter((t) => t.priority === priority);
    });
    grouped['Completed'] = completedTasks;
    return grouped;
  };

  const TASK_EXPORT_COLUMNS = [
    { header: 'Title', key: 'title' },
    { header: 'Category', key: 'category' },
    { header: 'Priority', key: 'priority' },
    { header: 'Status', key: 'status' },
    { header: 'Assigned To', key: 'assigned_to' },
    { header: 'Work Assignment', key: 'work_assignment' },
    { header: 'Due Date', key: 'due_date' },
    { header: 'Created', key: (r) => r.created_at?.split('T')[0] || '' },
    { header: 'Description', key: 'description' },
  ];

  const getExportData = () => filteredTasks;

  const handleRefresh = () => {
    setRefreshing(true);
    fetchTasks();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const groupedTasks = groupTasksByPriority();

  return (
    <div className="space-y-6">
      {/* Personal Reminders - Only visible to current user */}
      <PersonalReminders user={user} />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tasks</h1>
          <p className="text-slate-600 mt-1">{filteredTasks.length} tasks total</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* Date Range Filter */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-sm">
            <span className="text-xs text-slate-500 mr-1">From</span>
            <input
              type="date"
              value={dateRange.from}
              onChange={e => setDateRange(r => ({ ...r, from: e.target.value }))}
              className="text-xs border-0 outline-none bg-transparent text-slate-700"
              data-testid="tasks-date-from"
            />
            <span className="text-xs text-slate-400 mx-1">—</span>
            <span className="text-xs text-slate-500 mr-1">To</span>
            <input
              type="date"
              value={dateRange.to}
              onChange={e => setDateRange(r => ({ ...r, to: e.target.value }))}
              className="text-xs border-0 outline-none bg-transparent text-slate-700"
              data-testid="tasks-date-to"
            />
            {(dateRange.from || dateRange.to) && (
              <button onClick={() => setDateRange({ from: '', to: '' })} className="ml-1 text-slate-400 hover:text-slate-600">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <ExportButton
            rows={getExportData()}
            columns={TASK_EXPORT_COLUMNS}
            filename={`tasks-report-${new Date().toISOString().slice(0,10)}`}
            title="Tasks Report"
            subtitle={dateRange.from || dateRange.to ? `${dateRange.from || ''} to ${dateRange.to || ''}` : ''}
          />
          <Button
            onClick={handleRefresh}
            variant="outline"
            size="sm"
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            size="sm"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          {(user?.is_super_admin || user?.is_admin || user?.permissions?.tasks?.edit) && (
          <Button
            onClick={() => {
              setEditingTask(null);
              setIsModalOpen(true);
            }}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="create-task-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Task
          </Button>
          )}
        </div>
      </div>

      {showFilters && (
        <FilterBar filters={filters} setFilters={setFilters} searchValue={taskSearch} onSearchChange={setTaskSearch} />
      )}

      {/* Pending Task Deletion Requests — approvers only */}
      {canApproveTaskDel && taskDeleteRequests.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2" data-testid="pending-task-delete-requests">
          <div className="flex items-center gap-2 mb-2">
            <Trash2 className="h-4 w-4 text-red-600" />
            <h3 className="font-semibold text-red-800 text-sm">
              Pending Task Deletion Requests ({taskDeleteRequests.length})
            </h3>
          </div>
          {taskDeleteRequests.map(req => (
            <div key={req.id} className="flex items-start justify-between bg-white rounded border border-red-100 px-3 py-2 gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{req.record_display}</p>
                <p className="text-xs text-slate-500">
                  By {req.requested_by_name} · {req.created_at ? new Date(req.created_at).toLocaleDateString('en-IN') : ''}
                </p>
                {req.reason && (
                  <p className="text-xs text-slate-600 italic mt-0.5">Reason: {req.reason}</p>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" className="h-7 text-xs text-green-700 border-green-300 hover:bg-green-50" onClick={() => handleApproveTaskDelete(req.id)} data-testid={`approve-task-delete-${req.id}`}>Approve</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-50" onClick={() => handleRejectTaskDelete(req.id)} data-testid={`reject-task-delete-${req.id}`}>Reject</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Task Groups */}
      {filteredTasks.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border">
          <CheckSquare className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500">No tasks found. Create your first task!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedTasks).map(([priority, taskList]) => {
            if (taskList.length === 0 && priority !== 'Completed') return null;
            
            const isExpanded = expandedSections[priority];
            const priorityColors = {
              Critical: 'border-red-500 bg-red-50',
              High: 'border-orange-500 bg-orange-50',
              Medium: 'border-yellow-500 bg-yellow-50',
              Low: 'border-green-500 bg-green-50',
              Completed: 'border-slate-400 bg-slate-50',
            };

            return (
              <div key={priority} className={`rounded-lg border-l-4 ${priorityColors[priority]} overflow-hidden`}>
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white/50 transition-colors"
                  onClick={() => toggleSection(priority)}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-slate-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-500" />
                    )}
                    <h3 className="font-semibold text-slate-900">{priority}</h3>
                    <span className="px-2 py-0.5 text-xs font-medium bg-white rounded border">
                      {taskList.length}
                    </span>
                  </div>
                </div>

                {isExpanded && taskList.length > 0 && (
                  <div className="bg-white border-t">
                    <table className="w-full">
                      <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                        <tr>
                          <th className="px-4 py-2 text-left w-10">
                            <input
                              type="checkbox"
                              checked={taskList.every((t) => selectedTasks.includes(t.id))}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedTasks((prev) => [...new Set([...prev, ...taskList.map((t) => t.id)])]);
                                } else {
                                  setSelectedTasks((prev) => prev.filter((id) => !taskList.map((t) => t.id).includes(id)));
                                }
                              }}
                              className="rounded border-slate-300"
                            />
                          </th>
                          <th className="px-4 py-2 text-left">Task</th>
                          <th className="px-4 py-2 text-left">Category</th>
                          <th className="px-4 py-2 text-left">Status</th>
                          <th className="px-4 py-2 text-left">Assigned To</th>
                          <th className="px-4 py-2 text-left">Work</th>
                          <th className="px-4 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {taskList.map((task) => (
                          <TaskTableRow
                            key={task.id}
                            task={task}
                            isSelected={selectedTasks.includes(task.id)}
                            onToggleSelect={() => toggleTaskSelection(task.id)}
                            onEdit={(t) => {
                              setEditingTask(t);
                              setIsModalOpen(true);
                            }}
                            onDelete={handleDeleteTask}
                            onStatusChange={handleStatusChange}
                            canEdit={!!(user?.is_super_admin || user?.is_admin || user?.permissions?.tasks?.edit)}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {isExpanded && taskList.length === 0 && (
                  <div className="bg-white border-t px-4 py-6 text-center text-slate-500 text-sm">
                    No {priority.toLowerCase()} tasks
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <TaskModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTask(null);
        }}
        onSubmit={editingTask ? (data) => handleUpdateTask(editingTask.id, data) : handleCreateTask}
        task={editingTask}
      />

      {/* Floating Bulk Actions Toolbar */}
      {selectedTasks.length > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-slate-900 text-white rounded-2xl px-5 py-3 shadow-2xl border border-slate-700"
          style={{ animation: 'slideUp 0.2s ease-out' }}
          data-testid="bulk-actions-toolbar"
        >
          <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-200 border-r border-slate-700 pr-3">
            <CheckCheck className="w-4 h-4 text-blue-400" />
            {selectedTasks.length} selected
          </span>

          <div className="flex items-center gap-2">
            <Select onValueChange={handleBulkStatusChange} data-testid="bulk-status-select">
              <SelectTrigger className="h-8 w-44 text-xs bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700">
                <Tag className="w-3 h-3 mr-1 text-slate-400" />
                <SelectValue placeholder="Change status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Mark as Pending</SelectItem>
                <SelectItem value="in-progress">Mark as In Progress</SelectItem>
                <SelectItem value="order-placed">Mark as Order Placed</SelectItem>
                <SelectItem value="in-transit">Mark as In Transit</SelectItem>
                <SelectItem value="completed">Mark as Completed</SelectItem>
                <SelectItem value="blocked">Mark as Blocked</SelectItem>
              </SelectContent>
            </Select>

            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30 transition-colors"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>

            <button
              onClick={() => setSelectedTasks([])}
              className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-slate-700 transition-colors text-slate-400"
              data-testid="bulk-clear-selection"
              title="Clear selection"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translate(-50%, 16px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>

      {/* Task Delete Request Dialog — for non-admin users */}
      <Dialog open={isTaskDeleteDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsTaskDeleteDialogOpen(false);
          setTaskToDelete(null);
          setTaskDeleteReason('');
          setTaskDeleteImage(null);
          setTaskDeleteImagePreview(null);
          if (taskDeleteImageRef.current) taskDeleteImageRef.current.value = '';
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Request Task Deletion
            </DialogTitle>
          </DialogHeader>
          {taskToDelete && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <p className="font-semibold">{taskToDelete.title || `Task ID: ${taskToDelete.id}`}</p>
                {taskToDelete.category && <p className="text-xs mt-0.5">{taskToDelete.category} &mdash; {taskToDelete.priority}</p>}
              </div>
              <div>
                <Label>Reason for Deletion <span className="text-red-500">*</span></Label>
                <Textarea
                  className="mt-1.5 resize-none"
                  rows={3}
                  placeholder="Why should this task be deleted? (e.g. duplicate, created by mistake, task no longer relevant)..."
                  value={taskDeleteReason}
                  onChange={(e) => setTaskDeleteReason(e.target.value)}
                  data-testid="task-delete-reason-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1.5">
                  <ImagePlus className="h-4 w-4 text-slate-500" />
                  Proof Image <span className="text-slate-400 text-xs ml-1">(Optional)</span>
                </Label>
                <div
                  className="mt-1.5 border-2 border-dashed border-slate-200 rounded-lg p-3 text-center cursor-pointer hover:border-slate-400 transition-colors"
                  onClick={() => taskDeleteImageRef.current?.click()}
                >
                  {taskDeleteImagePreview ? (
                    <div className="relative">
                      <img src={taskDeleteImagePreview} alt="Preview" className="max-h-24 mx-auto rounded object-contain" />
                      <button type="button" className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5"
                        onClick={(e) => { e.stopPropagation(); setTaskDeleteImage(null); setTaskDeleteImagePreview(null); if (taskDeleteImageRef.current) taskDeleteImageRef.current.value = ''; }}>
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <ImagePlus className="h-7 w-7 mx-auto text-slate-300 mb-1" />
                      <p className="text-xs text-slate-500">Attach proof image (optional)</p>
                    </div>
                  )}
                </div>
                <input ref={taskDeleteImageRef} type="file" accept="image/*" className="hidden" onChange={handleTaskDeleteImageChange} />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsTaskDeleteDialogOpen(false)} disabled={submittingTaskDelete}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={submitTaskDeleteRequest}
              disabled={submittingTaskDelete || !taskDeleteReason.trim()}
              data-testid="submit-task-delete-request-btn"
            >
              {submittingTaskDelete ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksPage;
